import { OfflineTables } from '@/assets/ts/offline/DynamicTables';

/**
 * A classe SQLHelper fornece métodos auxiliares para a formatação e manipulação de consultas SQL.
 * 
 * Métodos:
 * 
 * - `getTableColumns(tableName: string): string[]`: Retorna uma lista de colunas para uma tabela específica a partir das definições de tabelas offline (DynamicTables.ts).
 * - `getValuesToQuery(columns: string[], values: {}): string[]`: Gera um array de valores correspondentes às colunas especificadas.
 * - `formatColumnNamesToCreate(columns: string[]): string`: Formata os nomes das colunas para a criação de uma tabela SQL, adicionando o tipo de dado `TEXT`.
 * - `formatColumnNames(columns: string[]): string`: Formata os nomes das colunas em uma string separada por vírgulas.
 * - `generateSQLPlaceholders(columns: string[]): string`: Gera uma string de placeholders SQL para inserção de valores, incluindo a coluna `ID`.
 */
export class SQLHelper {
  /**
   * Retorna uma lista de colunas para uma tabela específica a partir das definições de tabelas offline (DynamicTables.ts).
   * @param tableName O nome da tabela.
   * @returns Uma lista de nomes de colunas.
  */
  public static getTableColumns(tableName: string): string[] {
    const table = OfflineTables.find(({ name }) => name === tableName);
    return table ? table.columns : [];
  }

  /**
   * Gera um array de valores correspondentes às colunas especificadas.
   * @param columns Uma lista de nomes de colunas.
   * @param values Um objeto contendo os valores correspondentes às colunas.
   * @returns Uma lista de valores correspondentes às colunas.
  */
  public static getValuesToQuery(columns: string[], values: {}): string[] {
    const valuesQuery = columns.map((column) => values[column]);
    return valuesQuery;
  }

  /**
   * Formata os nomes das colunas para a criação de uma tabela SQL, adicionando o tipo de dado `TEXT`.
   * @param columns Uma lista de nomes de colunas.
   * @returns Uma string formatada com os nomes das colunas e o tipo de dado.
  */
  public static formatColumnNamesToCreate(columns: string[]): string {
    return columns.map(column => `${column} TEXT`).join(', ');
  }

  /**
   * Formata os nomes das colunas em uma string separada por vírgulas.
   * @param columns Uma lista de nomes de colunas.
   * @returns Uma string formatada com os nomes das colunas.
  */
  public static formatColumnNames(columns: string[]): string {
    return columns.join(', ');
  }

  /**
   * Gera uma string de placeholders SQL para inserção de valores, incluindo a coluna `ID`.
   * @param columns Uma lista de nomes de colunas.
   * @returns Uma string de placeholders SQL.
  */
  public static generateSQLPlaceholders(columns: string[]): string {
    const columnsWithId = ['ID', ...columns];
    return columnsWithId.map(() => '?').join(',');
  }
}
